import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useLocation } from "react-router-dom";
import {
  Avatar,
  Box,
  Typography,
  Button,
  MenuItem,
  Link,
  Divider,
  TextField,
  IconButton,
  Drawer,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { styled } from "@mui/material/styles";
import Menu, { MenuProps } from "@mui/material/Menu";
import LogoutIcon from "@mui/icons-material/Logout";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import HomeIcon from "@mui/icons-material/HomeOutlined";
import SearchIcon from "@mui/icons-material/Search";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import SettingsOutlined from "@mui/icons-material/SettingsOutlined";
import ShoppingCartOutlined from "@mui/icons-material/ShoppingCartOutlined";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import PublicIcon from "@mui/icons-material/Public";

import { useMutation, useManualQuery } from "graphql-hooks";
import { CURRENT_USER_DATA_BY_EMAIL } from "./graphQL/user_qry";

// Import logo
import logo from "./assets/logo.png";
import avatar from "./assets/avatar.png";

// Main Menu Items
const mainMenuItems = [
  {
    id: "dashboard",
    label: "Dashboard",
    icon: HomeIcon,
    link: "/dashboard/",
  },
  {
    id: "profile",
    label: "My Profile",
    icon: PersonOutlineIcon,
    link: "/my-profile/",
  },
  {
    id: "publicpage",
    label: "My Public Page",
    icon: PublicIcon,
    link: "/user/[uuid]/profile",
    requiresUuid: true,
  },
  {
    id: "workarea",
    label: "My Work Area",
    icon: SettingsOutlined,
    hasSubmenu: true,
  },
  {
    id: "logout",
    label: "Logout",
    icon: LogoutIcon,
    link: "/signout/",
  },
];

// Work Area Submenu Structure
const workAreaSubmenu = {
  title: "My Work Area",
  sections: [
    {
      id: "my-activities-section",
      title: "My Activities",
      icon: PersonOutlineIcon,
      items: [
        { id: "profile", label: "Profile", link: "/my-profile/" },
        { id: "toys-for-sales", label: "Toys for Sales", link: "/my-toys" },
        {
          id: "toys-purchased",
          label: "Toys Purchased",
          link: "/mytoys-purchased",
        },
        { id: "ratings", label: "Ratings", link: "/my-ratings" },
        { id: "stories", label: "Stories", link: "/my-stories" },
        { id: "videos", label: "Videos", link: "/my-videos" },
        { id: "followers", label: "Followers", link: "my-followers" },
        { id: "followings", label: "Followings", link: "/my-followings" },
        {
          id: "collections",
          label: "Collections",
          link: "/my-collections/",
        },
      ],
    },
    //We will activate them after going live and in the future versions.
    // {
    //   id: "transactions-section",
    //   title: "Transactions",
    //   icon: ShoppingCartOutlined,
    //   items: [
    //     {
    //       id: "my-purchases",
    //       label: "My Purchases",
    //       link: "/mytoys-purchased/",
    //     },
    //     { id: "my-sales", label: "My Sales", link: "/my-toys/" },
    //     { id: "order-activity", label: "Order Activity", link: "" },
    //   ],
    // },
    // {
    //   id: "settings-section",
    //   title: "Settings",
    //   icon: SettingsOutlined,
    //   items: [
    //     { id: "account-settings", label: "Account Settings", link: "" },
    //     { id: "notifications", label: "Notifications", link: "" },
    //     { id: "security", label: "Security", link: "" },
    //   ],
    // },
  ],
};

const StyledMenu = styled((props) => (
  <Menu
    elevation={0}
    anchorOrigin={{
      vertical: "bottom",
      horizontal: "right",
    }}
    transformOrigin={{
      vertical: "top",
      horizontal: "right",
    }}
    {...props}
  />
))(({ theme }) => ({
  "& .MuiPaper-root": {
    borderRadius: 6,
    marginTop: theme.spacing(1),
    minWidth: 160,
    color:
      theme.palette.mode === "light"
        ? "rgb(55, 65, 81)"
        : theme.palette.grey[300],
    boxShadow:
      "rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px",
    "& .MuiMenu-list": {
      padding: "4px 0",
    },
    "& .MuiMenuItem-root": {
      fontSize: 14,
      marginTop: theme.spacing(1),
      "& .MuiSvgIcon-root": {
        fontSize: 24,
        marginRight: theme.spacing(1.5),
      },
    },
  },
}));

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState(true);
  const [displaySearchBar, setDisplaySearchBar] = useState(true);
  const [keyword, setKeyword] = useState("");
  const [avatarPath, setAvatarPath] = useState("");

  let email = localStorage.getItem("email")
    ? localStorage.getItem("email")
    : null;
  let userName = localStorage.getItem("userName")
    ? localStorage.getItem("userName")
    : "";
  let userUuid = localStorage.getItem("userUuid")
    ? localStorage.getItem("userUuid")
    : "";

  const [getCurrUsrData] = useManualQuery(CURRENT_USER_DATA_BY_EMAIL);

  useEffect(() => {
    (async () => setCurrentUserInfo())();
  }, [email]);

  const setCurrentUserInfo = async () => {
    if (!email) return;
    const {
      loading: loadingCurrUsrInfo,
      error: errorCurrUsrInfo,
      data: currUsrInfo,
    } = await getCurrUsrData({
      variables: {
        email,
      },
    });
    if (!loadingCurrUsrInfo && currUsrInfo) {
      setAvatarPath(currUsrInfo.currentUserDataByEmail.avatarFileName);
    }
  };
  /// detect authentication
  useEffect(() => {
    if (email) {
      setIsAuthenticated(true);
    } else {
      setIsAuthenticated(false);
    }
    const path = location.pathname;
    if (
      path.includes("signin") ||
      path.includes("signup") ||
      path.includes("password")
    ) {
      setDisplaySearchBar(false);
    } else {
      setDisplaySearchBar(true);
    }
  }, [email, location.pathname]);

  /// Main Menu
  const [mainMenuAnchorEl, setMainMenuAnchorEl] = useState(null);
  const mainMenuOpen = Boolean(mainMenuAnchorEl);
  const handleMainMenuClick = (event) => {
    setMainMenuAnchorEl(event.currentTarget);
  };
  const handleMainMenuClose = () => {
    setWorkAreaAnchorEl(null);
    setMainMenuAnchorEl(null);
  };

  /// Work Area Menu
  const [workAreaAnchorEl, setWorkAreaAnchorEl] = useState(null);
  const workAreaOpen = Boolean(workAreaAnchorEl);
  const handleWorkAreaClick = (event) => {
    setWorkAreaAnchorEl(event.currentTarget);
  };
  const handleWorkAreaClose = () => {
    setWorkAreaAnchorEl(null);
  };

  useEffect(() => {
    const handleResize = () => {
      setMainMenuAnchorEl(null);
      setWorkAreaAnchorEl(null);
      setMobileMenuOpen(false);
    };

    const handleScroll = () => {
      setMainMenuAnchorEl(null);
      setWorkAreaAnchorEl(null);
    };

    window.addEventListener("resize", handleResize);
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  /// Mobile Drawer
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileWorkAreaOpen, setMobileWorkAreaOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});

  const handleMobileMenuOpen = () => {
    setMobileMenuOpen(true);
  };
  const handleMobileMenuClose = () => {
    setMobileMenuOpen(false);
    setMobileWorkAreaOpen(false);
    setExpandedSections({});
  };
  const toggleMobileWorkArea = () => {
    setMobileWorkAreaOpen(!mobileWorkAreaOpen);
    if (mobileWorkAreaOpen) {
      setExpandedSections({});
    }
  };
  const toggleSection = (sectionId) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  };

  const handleSearch = () => {
    if (!keyword.trim()) return;
    window.location.href = `/search-results?q=${encodeURIComponent(
      keyword.trim()
    )}`;
  };

  return (
    <>
      {/* Header */}
      <Box
        sx={{
          backgroundColor: "white",
          borderBottom: isAuthenticated ? "1px solid #E0E0E0" : "none",
          boxShadow: !isAuthenticated ? "0 5px 8px rgba(0, 0, 0, 0.1)" : "none",
          py: 2,
          px: "100px",
          position: "relative",
          "@media (max-width: 1360px)": {
            px: "16px",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          {/* Logo */}
          {isAuthenticated && (
            <Link
              component={RouterLink}
              to="/dashboard"
              style={{ textDecoration: "none", color: "inherit" }}
            >
              <Box
                component="img"
                src={logo}
                alt="R3PLAY Logo"
                sx={{
                  height: "50px",
                  width: "161px",
                }}
              />
            </Link>
          )}
          {!isAuthenticated && (
            <Box
              component="img"
              src={logo}
              alt="R3PLAY Logo"
              sx={{
                height: "50px",
                width: "161px",
              }}
            />
          )}

          {/* Navigation Items - Corporate Pages */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 3,
              "@media (max-width: 1024px)": {
                paddingLeft: "100px",
              },
              "@media (max-width: 906px)": {
                display: "none",
              },
            }}
          >
            <Typography
              sx={{
                color: "#333",
                fontSize: "16px",
                cursor: "pointer",
                "&:hover": {
                  color: "#EF5658",
                },
              }}
              onClick={() => navigate("/about")}
            >
              About
            </Typography>
            <Typography
              sx={{
                color: "#333",
                fontSize: "16px",
                cursor: "pointer",
                "&:hover": {
                  color: "#EF5658",
                },
              }}
              onClick={() => navigate("/how-r3play-works")}
            >
              How R3PLAY Works
            </Typography>
            <Typography
              sx={{
                color: "#333",
                fontSize: "16px",
                cursor: "pointer",
                "&:hover": {
                  color: "#EF5658",
                },
              }}
              onClick={() => navigate("/contact")}
            >
              Contact
            </Typography>

            {/* Action Buttons */}
            {!isAuthenticated && (
              <Button
                variant="contained"
                onClick={() => navigate("/signin")}
                sx={{
                  backgroundColor: "#EF5658",
                  color: "white",
                  borderRadius: "20px",
                  px: 3,
                  py: 1,
                  fontSize: "14px",
                  fontWeight: "bold",
                  textTransform: "uppercase",
                  "&:hover": {
                    backgroundColor: "#D94446",
                  },
                }}
              >
                LOG IN
              </Button>
            )}
            {!isAuthenticated && (
              <Button
                variant="outlined"
                onClick={() => navigate("/signup")}
                sx={{
                  borderColor: "#EF5658",
                  color: "#EF5658",
                  borderRadius: "20px",
                  px: 3,
                  py: 1,
                  fontSize: "14px",
                  fontWeight: "bold",
                  textTransform: "uppercase",
                  "&:hover": {
                    borderColor: "#EF5658",
                    backgroundColor: "rgba(239, 86, 88, 0.1)",
                  },
                }}
              >
                SIGN UP
              </Button>
            )}
            {isAuthenticated && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  "@media (max-width: 1024px)": {
                    display: "none",
                  },
                }}
              >
                <Avatar
                  src={avatarPath}
                  alt="Profile"
                  sx={{ width: 40, height: 40 }}
                />
                <Button
                  aria-haspopup="true"
                  aria-expanded={mainMenuOpen ? "true" : undefined}
                  onClick={handleMainMenuClick}
                  endIcon={
                    mainMenuOpen ? (
                      <KeyboardArrowUpIcon />
                    ) : (
                      <KeyboardArrowDownIcon />
                    )
                  }
                  sx={{
                    textTransform: "none",
                    color: "#000",
                    fontSize: "16px",
                    fontWeight: "500",
                    "&:hover": {
                      backgroundColor: "transparent",
                    },
                  }}
                >
                  {userName}
                </Button>

                <StyledMenu
                  anchorEl={mainMenuAnchorEl}
                  open={mainMenuOpen}
                  onClose={handleMainMenuClose}
                  disableScrollLock
                >
                  {mainMenuItems.map((item, index) => (
                    <React.Fragment key={item.id}>
                      {item.hasSubmenu ? (
                        <MenuItem
                          onClick={handleWorkAreaClick}
                          disableRipple
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            backgroundColor: workAreaOpen
                              ? "rgba(239, 86, 88, 0.1)"
                              : "transparent",
                            color: workAreaOpen ? "#EF5658" : "#333",
                            "&:hover": {
                              backgroundColor: "rgba(239, 86, 88, 0.1)",
                            },
                          }}
                        >
                          <item.icon
                            sx={{ color: workAreaOpen ? "#EF5658" : "#666" }}
                          />
                          {item.label}
                          <KeyboardArrowDownIcon
                            sx={{
                              mt: "3px",
                              ml: "10px",
                              transform: "rotate(-90deg)",
                            }}
                          />
                        </MenuItem>
                      ) : (
                        <Link
                          component={RouterLink}
                          to={
                            item.requiresUuid
                              ? item.link.replace("[uuid]", userUuid)
                              : item.link
                          }
                          style={{ textDecoration: "none", color: "inherit" }}
                        >
                          <MenuItem onClick={handleMainMenuClose} disableRipple>
                            <item.icon sx={{ color: "#666" }} />
                            {item.label}
                          </MenuItem>
                        </Link>
                      )}
                      {index < mainMenuItems.length - 1 &&
                        index !== 1 &&
                        index !== 2 && <Divider sx={{ my: 0.5 }} />}
                    </React.Fragment>
                  ))}
                </StyledMenu>

                {/* Work Area Submenu */}
                <Menu
                  anchorEl={workAreaAnchorEl}
                  open={workAreaOpen && mainMenuOpen}
                  onClose={handleWorkAreaClose}
                  disableScrollLock
                  anchorOrigin={{
                    vertical: "top",
                    horizontal: "left",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                  }}
                  slotProps={{
                    paper: {
                      sx: {
                        marginRight: "20px",
                        maxHeight: "500px",
                        overflowY: "auto",
                        scrollbarWidth: "thin",
                      },
                    },
                  }}
                  sx={{
                    "& .MuiPaper-root": {
                      borderRadius: 2,
                      minWidth: 250,
                      boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
                    },
                  }}
                >
                  <Box>
                    <Box sx={{ px: 2, pt: 1 }}>
                      <Typography
                        sx={{
                          fontSize: "20px",
                          fontWeight: "600",
                          color: "#333",
                          mb: 1,
                        }}
                      >
                        {workAreaSubmenu.title}
                      </Typography>
                    </Box>

                    <Divider sx={{ mb: 2 }} />

                    {workAreaSubmenu.sections.map((section, sectionIndex) => (
                      <React.Fragment key={section.id}>
                        <Box sx={{ mb: 1, px: 2 }}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              mb: 1,
                            }}
                          >
                            <section.icon
                              sx={{ fontSize: 24, color: "#666" }}
                            />
                            <Typography
                              sx={{
                                fontSize: "16px",
                                fontWeight: "700",
                                color: "#333",
                              }}
                            >
                              {section.title}
                            </Typography>
                          </Box>
                          {section.items.map((item) => (
                            <Typography
                              key={item.id}
                              onClick={() => {
                                if (item.link) {
                                  navigate(item.link);
                                  handleWorkAreaClose();
                                  handleMainMenuClose();
                                }
                              }}
                              sx={{
                                fontSize: "14px",
                                fontWeight: "400",
                                color: "#666",
                                pl: 4,
                                py: 0.5,
                                cursor: "pointer",
                                "&:hover": {
                                  color: "#EF5658",
                                },
                              }}
                            >
                              {item.label}
                            </Typography>
                          ))}
                        </Box>
                        {sectionIndex < workAreaSubmenu.sections.length - 1 && (
                          <Divider sx={{ my: 1.5 }} />
                        )}
                      </React.Fragment>
                    ))}
                  </Box>
                </Menu>
              </Box>
            )}
          </Box>

          {/* Tablet Navigation */}
          <Box
            sx={{
              display: "none",
              alignItems: "center",
              gap: 2,
              "@media (max-width: 1024px)": {
                display: isAuthenticated ? "flex" : "none",
              },
              "@media (max-width: 906px)": {
                display: "none",
              },
            }}
          >
            <Avatar
              src={avatarPath}
              alt="Profile"
              sx={{ width: 40, height: 40 }}
            />
            <Typography
              sx={{
                color: "#333",
                fontSize: "16px",
                fontWeight: "500",
              }}
            >
              {userName}
            </Typography>
            <IconButton
              onClick={handleMobileMenuOpen}
              sx={{
                color: "#999",
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.04)",
                },
              }}
            >
              <MenuIcon />
            </IconButton>
          </Box>

          {/* Mobile Navigation */}
          <Box
            sx={{
              display: "none",
              alignItems: "center",
              gap: 2,
              "@media (max-width: 906px)": {
                display: "flex",
              },
            }}
          >
            {!isAuthenticated && (
              <Button
                variant="contained"
                onClick={() => navigate("/signin")}
                sx={{
                  backgroundColor: "#EF5658",
                  color: "white",
                  borderRadius: "20px",
                  px: 3,
                  py: 1,
                  fontSize: "14px",
                  fontWeight: "bold",
                  textTransform: "uppercase",
                  "&:hover": {
                    backgroundColor: "#D94446",
                  },
                }}
              >
                LOG IN
              </Button>
            )}
            {isAuthenticated && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Avatar
                  src={avatarPath}
                  alt="Profile"
                  sx={{ width: 40, height: 40 }}
                />
                <Typography
                  sx={{
                    color: "#333",
                    fontSize: "16px",
                    fontWeight: "500",
                  }}
                >
                  {userName}
                </Typography>
              </Box>
            )}
            <IconButton
              onClick={handleMobileMenuOpen}
              sx={{
                color: "#999",
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.04)",
                },
              }}
            >
              <MenuIcon />
            </IconButton>
          </Box>
        </Box>
      </Box>

      {/* Search Bar Section
      {/*displaySearchBar &&  //RB 2025-12-14*/}
      {isAuthenticated && (
        <Box
          sx={{
            backgroundColor: "white",
            borderBottom: "1px solid #E0E0E0",
            py: 2,
            px: "100px",
            boxShadow: "0 5px 8px rgba(0, 0, 0, 0.1)",
            position: "relative",
            zIndex: 1,
            "@media (max-width: 1360px)": {
              px: "16px",
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              "@media (max-width: 906px)": {
                flexDirection: "column",
                gap: 1,
                alignItems: "flex-start",
              },
            }}
          >
            {/* Discover Toys Title */}
            <Typography
              sx={{
                fontSize: "24px",
                fontWeight: "bold",
                color: "#333",
                "@media (max-width: 906px)": {
                  fontSize: "20px",
                  mb: 1,
                },
              }}
            >
              Discover Toys
            </Typography>

            {/* Search Controls with fixed widths */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0,
                "@media (max-width: 906px)": {
                  width: "100%",
                },
              }}
            >
              {/* Search Input */}
              <TextField
                placeholder="Search for toys"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                variant="outlined"
                sx={{
                  width: 365,
                  "@media (max-width: 906px)": {
                    flex: 1,
                    width: "auto",
                    flexWrap: "wrap",
                  },
                  "& .MuiOutlinedInput-root": {
                    height: "40px",
                    backgroundColor: "#f5f5f5",
                    borderRadius: "0",
                    fontSize: "14px",
                    "@media (max-width: 906px)": {
                      fontSize: "16px",
                    },
                    "& fieldset": {
                      border: "1px solid #ddd",
                      borderRight: "none",
                    },
                    "&:hover fieldset": {
                      borderColor: "#ddd !important",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#ddd !important",
                    },
                  },
                }}
              />

              {/* Search Button with Icon */}
              <IconButton
                onClick={handleSearch}
                sx={{
                  backgroundColor: "#EF5658",
                  color: "white",
                  width: 40,
                  height: 40,
                  borderRadius: "0 4px 4px 0",
                  "&:hover": {
                    backgroundColor: "#D94446",
                  },
                }}
              >
                <SearchIcon />
              </IconButton>
            </Box>
          </Box>
        </Box>
      )}

      {/* Mobile Drawer Menu */}
      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={handleMobileMenuClose}
        disableScrollLock={false}
        sx={{
          zIndex: 2100,
          "& .MuiDrawer-paper": {
            width: "280px",
            backgroundColor: "white",
            //  zIndex: 2100,
          },
          "& .MuiBackdrop-root": {
            //  zIndex: 2050,
          },
        }}
      >
        <Box
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            overflowY: "auto",
            scrollbarWidth: "thin",
            scrollbarColor: "#d0d0d0 transparent",
          }}
        >
          {isAuthenticated ? (
            <>
              {/* Header with User Info */}
              <Box
                sx={{
                  p: 2,
                  borderBottom: "1px solid #E0E0E0",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Avatar
                    src={avatarPath}
                    alt="Profile"
                    sx={{ width: 50, height: 50 }}
                  />
                  <Box>
                    <Typography
                      sx={{
                        color: "#333",
                        fontSize: "18px",
                        fontWeight: "600",
                      }}
                    >
                      {userName}
                    </Typography>
                    <Typography
                      sx={{
                        color: "#666",
                        fontSize: "14px",
                      }}
                    >
                      @{userName.toLowerCase()}
                    </Typography>
                  </Box>
                </Box>
                <IconButton onClick={handleMobileMenuClose}>
                  <CloseIcon />
                </IconButton>
              </Box>

              {/* Menu Items */}
              <Box>
                {mainMenuItems.map((item, index) => (
                  <React.Fragment key={item.id}>
                    {item.hasSubmenu ? (
                      <>
                        <Box
                          sx={{
                            border: mobileWorkAreaOpen
                              ? "1px solid #EF5658"
                              : "none",
                            borderRadius: mobileWorkAreaOpen ? "8px" : "0",
                            mx: mobileWorkAreaOpen ? "4px" : 0,
                            overflow: "hidden",
                          }}
                        >
                          <Box
                            onClick={toggleMobileWorkArea}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 2,
                              px: mobileWorkAreaOpen ? "20px" : 3,
                              py: 1,
                              cursor: "pointer",
                              backgroundColor: mobileWorkAreaOpen
                                ? "rgba(239, 86, 88, 0.1)"
                                : "transparent",
                              "&:hover": {
                                backgroundColor: "rgba(239, 86, 88, 0.1)",
                              },
                            }}
                          >
                            <item.icon
                              sx={{
                                color: mobileWorkAreaOpen ? "#EF5658" : "#666",
                                fontSize: 24,
                              }}
                            />
                            <Typography
                              sx={{
                                color: mobileWorkAreaOpen ? "#EF5658" : "#333",
                                fontSize: "14px",
                              }}
                            >
                              {item.label}
                            </Typography>
                            <Box sx={{ ml: "auto" }}>
                              {mobileWorkAreaOpen ? (
                                <KeyboardArrowUpIcon
                                  sx={{ color: "#EF5658" }}
                                />
                              ) : (
                                <KeyboardArrowDownIcon sx={{ color: "#666" }} />
                              )}
                            </Box>
                          </Box>

                          {/* Expandable Submenu */}
                          {mobileWorkAreaOpen && (
                            <Box sx={{ backgroundColor: "#f9f9f9" }}>
                              {workAreaSubmenu.sections.map((section) => (
                                <Box key={section.id} sx={{ mb: 0.5 }}>
                                  <Box
                                    onClick={() => toggleSection(section.id)}
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                      pr: 3,
                                      pl: "20px",
                                      py: 1.5,
                                      cursor: "pointer",
                                      "&:hover": {
                                        backgroundColor: "rgba(0, 0, 0, 0.03)",
                                      },
                                    }}
                                  >
                                    <section.icon
                                      sx={{
                                        color: "#666",
                                        marginRight: "10px",
                                      }}
                                    />
                                    <Typography
                                      sx={{ fontSize: "14px", color: "#333" }}
                                    >
                                      {section.title}
                                    </Typography>
                                    <Box sx={{ ml: "auto" }}>
                                      {expandedSections[section.id] ? (
                                        <RemoveIcon
                                          sx={{ fontSize: 18, color: "#666" }}
                                        />
                                      ) : (
                                        <AddIcon
                                          sx={{ fontSize: 18, color: "#666" }}
                                        />
                                      )}
                                    </Box>
                                  </Box>
                                  {expandedSections[section.id] &&
                                    section.items.map((subItem) => (
                                      <Box
                                        key={subItem.id}
                                        onClick={() => {
                                          if (subItem.link) {
                                            navigate(subItem.link);
                                            handleMobileMenuClose();
                                          }
                                        }}
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 1,
                                          pl: 8,
                                          pr: 3,
                                          py: 0.75,
                                          cursor: "pointer",
                                          "&:hover": {
                                            color: "#EF5658",
                                            backgroundColor:
                                              "rgba(239, 86, 88, 0.05)",
                                          },
                                        }}
                                      >
                                        <Box
                                          sx={{
                                            width: 6,
                                            height: 6,
                                            borderRadius: "50%",
                                            backgroundColor: "#999",
                                            flexShrink: 0,
                                          }}
                                        />
                                        <Typography
                                          sx={{
                                            fontSize: "14px",
                                            fontWeight: "400",
                                            color: "#666",
                                          }}
                                        >
                                          {subItem.label}
                                        </Typography>
                                      </Box>
                                    ))}
                                </Box>
                              ))}
                            </Box>
                          )}
                        </Box>
                      </>
                    ) : (
                      <Link
                        component={RouterLink}
                        to={
                          item.requiresUuid
                            ? item.link.replace("[uuid]", userUuid)
                            : item.link
                        }
                        onClick={handleMobileMenuClose}
                        sx={{ textDecoration: "none" }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                            px: 3,
                            py: 2,
                            cursor: "pointer",
                            "&:hover": {
                              backgroundColor: "rgba(239, 86, 88, 0.1)",
                            },
                          }}
                        >
                          <item.icon sx={{ color: "#666", fontSize: 24 }} />
                          <Typography sx={{ color: "#333", fontSize: "14px" }}>
                            {item.label}
                          </Typography>
                        </Box>
                      </Link>
                    )}
                    {index < mainMenuItems.length - 1 &&
                      index !== 1 &&
                      index !== 2 && <Divider sx={{ my: 0.5 }} />}
                  </React.Fragment>
                ))}
              </Box>
              {/* Divider before corporate navigation */}
              <Divider />
              {/* Corporate Navigation Items */}
              <Box
                sx={{
                  py: 1.5,
                  pl: 8,
                  cursor: "pointer",
                  "&:hover": {
                    backgroundColor: "rgba(239, 86, 88, 0.1)",
                  },
                }}
                onClick={() => {
                  navigate("/about");
                  handleMobileMenuClose();
                }}
              >
                <Typography
                  sx={{
                    color: "#333",
                    fontSize: "14px",
                    fontWeight: "400",
                  }}
                >
                  About
                </Typography>
              </Box>

              <Box
                sx={{
                  py: 1.5,
                  pl: 8,
                  cursor: "pointer",
                  "&:hover": {
                    backgroundColor: "rgba(239, 86, 88, 0.1)",
                  },
                }}
                onClick={() => {
                  navigate("/how-r3play-works");
                  handleMobileMenuClose();
                }}
              >
                <Typography
                  sx={{
                    color: "#333",
                    fontSize: "14px",
                    fontWeight: "400",
                  }}
                >
                  How R3PLAY Works
                </Typography>
              </Box>

              <Box
                sx={{
                  py: 1.5,
                  pl: 8,
                  cursor: "pointer",
                  "&:hover": {
                    backgroundColor: "rgba(239, 86, 88, 0.1)",
                  },
                }}
                onClick={() => {
                  navigate("/contact");
                  handleMobileMenuClose();
                }}
              >
                <Typography
                  sx={{
                    color: "#333",
                    fontSize: "14px",
                    fontWeight: "400",
                  }}
                >
                  Contact
                </Typography>
              </Box>
            </>
          ) : (
            <>
              {/* Header for unauthenticated users */}
              <Box
                sx={{
                  p: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <img
                    src={logo}
                    alt="R3PLAY Logo"
                    style={{
                      height: "40px",
                      width: "auto"
                    }}
                  />
                </Box>
                <IconButton onClick={handleMobileMenuClose}>
                  <CloseIcon />
                </IconButton>
              </Box>

              {/* Divider below X button */}
              <Divider />

              {/* Corporate Page Links */}
              <Box
                sx={{
                  p: 2,
                  cursor: "pointer",
                  "&:hover": {
                    backgroundColor: "rgba(239, 86, 88, 0.1)",
                  },
                }}
                onClick={() => {
                  navigate("/about");
                  handleMobileMenuClose();
                }}
              >
                <Typography
                  sx={{
                    color: "#333",
                    fontSize: "14px",
                    fontWeight: "400",
                  }}
                >
                  About
                </Typography>
              </Box>
              <Box
                sx={{
                  p: 2,
                  cursor: "pointer",
                  "&:hover": {
                    backgroundColor: "rgba(239, 86, 88, 0.1)",
                  },
                }}
                onClick={() => {
                  navigate("/how-r3play-works");
                  handleMobileMenuClose();
                }}
              >
                <Typography
                  sx={{
                    color: "#333",
                    fontSize: "14px",
                    fontWeight: "400",
                  }}
                >
                  How R3PLAY Works
                </Typography>
              </Box>
              <Box
                sx={{
                  p: 2,
                  cursor: "pointer",
                  "&:hover": {
                    backgroundColor: "rgba(239, 86, 88, 0.1)",
                  },
                }}
                onClick={() => {
                  navigate("/contact");
                  handleMobileMenuClose();
                }}
              >
                <Typography
                  sx={{
                    color: "#333",
                    fontSize: "14px",
                    fontWeight: "400",
                  }}
                >
                  Contact
                </Typography>
              </Box>

              {/* Divider above Sign Up button */}
              <Divider />

              {/* Sign Up Button */}
              <Box sx={{ p: 3, display: "flex", justifyContent: "center" }}>
                <Button
                  variant="outlined"
                  //fullWidth
                  onClick={() => {
                    handleMobileMenuClose();
                    navigate("/signup");
                  }}
                  sx={{
                    borderColor: "#EF5658",
                    color: "#EF5658",
                    borderRadius: "20px",
                    px: 3,
                    py: 1,
                    fontSize: "14px",
                    fontWeight: "bold",
                    textTransform: "uppercase",
                    "&:hover": {
                      borderColor: "#EF5658",
                      backgroundColor: "rgba(239, 86, 88, 0.1)",
                    },
                  }}
                >
                  SIGN UP
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Drawer>
    </>
  );
};

export default Header;
